import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MaintenancesApi } from '@api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { AccountFeatureFlagNameEnum, Equipment, Maintenance, UploadCredentialEntityEnum } from '@core/interfaces';
import { UploadService } from '@core/services/upload.service';
import { OnlySpacesValidation } from '@validators/only-spaces';
import { ImportsService } from 'app/main/settings/imports/services/imports.service';
import { chain, isArray, map } from 'lodash';
import moment from 'moment';
import {
  ConcatSectorsEnvironmentsParams,
  CreateMaintenanceFormParams,
  MaintenanceAttachmentInput,
  MaintenanceAttachmentToSave,
  MaintenanceCreateInput,
  MaintenanceScheduleSuggestionInput,
  ToMaintenanceCreateInputParams
} from './dialog-create-maintenances.type';

@Injectable({
  providedIn: 'root'
})
export class DialogCreateMaintenancesService {
  public readonly notContainNumberSignRegex = new RegExp('^[^#]+$');

  public constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly importsService: ImportsService,
    private readonly uploadService: UploadService,
    private readonly formBuilder: FormBuilder,
    private readonly maintenancesApi: MaintenancesApi,
  ) {}

  public createMaintenanceForm(params: CreateMaintenanceFormParams): FormGroup {
    const maintenanceForm = this.formBuilder.group({
      number: new FormControl(null, [Validators.pattern(this.notContainNumberSignRegex), Validators.maxLength(30)]),
      segment: new FormControl(null, Validators.required),
      company: new FormControl({ value: null, disabled: true }),
      deadlineContract: new FormControl({ value: null, disabled: true }),
      address: new FormControl({ value: null, disabled: true }, Validators.required),
      maintenanceType: new FormControl(null, Validators.required),
      location: new FormControl(null, Validators.required),
      problem: new FormControl({ value: null, disabled: true }),
      message: new FormControl(null, [Validators.required, OnlySpacesValidation.validate]),
      attachments: new FormControl([]),
      equipments: new FormControl([]),
      searchMode: new FormControl('equipment'),
      scheduleSuggestions: new FormArray([]),
      scheduleSuggestionsStatus: new FormControl(null),
      warranties: new FormControl(null),
      previousMaintenanceId: new FormControl(null),
    });

    maintenanceForm.addControl('newEquipments', new FormControl([]));
    maintenanceForm.addControl('locationSectors', new FormControl([]));
    maintenanceForm.addControl('locationEnvironments', new FormControl([]));

    if (params.maintenanceNumberGeneratorType === 'none') {
      maintenanceForm.get('number').setValidators([
        Validators.required,
        Validators.pattern(this.notContainNumberSignRegex), Validators.maxLength(30)
      ]);

      maintenanceForm.get('number').updateValueAndValidity();
    }

    return maintenanceForm;
  }

  public toMaintenanceCreateInput(params: ToMaintenanceCreateInputParams): MaintenanceCreateInput {
    const value = params.form.getRawValue();
    const input = {
      number: value.number,
      message: value.message,
      attachments: params.attachmentsMapped,
      address: {
        zipCode: value.address.postalCode,
        street: value.address.streetName,
        city: value.address.city,
        number: value.address.number,
        complement: value.address.complement,
        neighborhood: value.address.neighborhood,
        state: value.address.state,
        coords: {
          latitude: value.address.coords.latitude,
          longitude: value.address.coords.longitude
        },
      },
      scheduleSuggestions: params.scheduleSuggestionsMapped,
      deadlineContractId: this.toId(value.deadlineContract),
      segmentId: this.toId(value.segment),
      problemId: this.toId(value.problem),
      companyId: this.toId(value.company),
      locationId: this.toId(value.location),
      typeId: this.toId(value.maintenanceType),
      equipmentIds: this.toIds(value.equipments),
      warranties: value.warranties,
      previousMaintenanceId: value.previousMaintenanceId,
    };

    return this.concatSectorsEnvironments({ input, value });
  }

  public toMaintenanceScheduleSuggestionsInput(form: FormGroup): MaintenanceScheduleSuggestionInput[] {
    const { scheduleSuggestions } = form.getRawValue();
    if (!scheduleSuggestions.length) {
      return null;
    }

    return scheduleSuggestions.map((suggestion: MaintenanceScheduleSuggestionInput) => {
      return {
        date: moment(suggestion.date).set({ hour: 12 }).format('YYYY-MM-DD'),
        time: suggestion.time
      };
    });
  }

  public async toMaintenanceAttachmentsInput(form: FormGroup): Promise<MaintenanceAttachmentInput[]> {
    const { attachments } = form.getRawValue();
    if (!attachments.length) {
      return null;
    }

    const result = attachments.map(async (attachment: MaintenanceAttachmentToSave) => {
      const credential = await this.importsService.createUploadCredential({
        size: attachment.size,
        extension: attachment.filename.split('.').pop(),
        entity: UploadCredentialEntityEnum.maintenances
      });

      await this.uploadService.uploadWithCredentials({
        fields: credential.fields,
        baseUrl: credential.baseUrl,
        file: attachment.file
      });

      return {
        path: credential.link,
        title: attachment.filename
      } as MaintenanceAttachmentInput;
    });

    return Promise.all(result);
  }

  public async hasPreviousMaintenanceWarranties(maintenanceForm: FormGroup): Promise<Maintenance[] | null> {
    const {segment, location, maintenanceType} = maintenanceForm.getRawValue();
    if (!segment.id || !location.id || !maintenanceType.id) { return null; }

    const where = {
      segmentId_in: [segment.id],
      locationId_in: [location.id],
      typeId_in: [maintenanceType.id],
      warrantyStatusIn: ['active', 'extended'],
      status_in: ['done'
    ]
    };

    const result = await this.maintenancesApi.search(where, { order: 'asc', orderColumn: 'openedAt', limit: 10 }).toPromise();
    if (!result.items.length) { return null; }

    return result.items;
  }

  private toId(value?: Record<string, any>): string {
    return (value && value.id) || null;
  }

  private toIds(values: Record<string, any>[]): string[] {
    return isArray(values) ? map(values, 'id') : null;
  }

  private concatSectorsEnvironments(params: ConcatSectorsEnvironmentsParams): MaintenanceCreateInput {
    const { input, value } = params;

    const { equipments = [], locationSectors= [], locationEnvironments = [], newEquipments = [] } = value;
    const concatLocationEnvironments = locationEnvironments;
    const concatEquipments = chain(equipments).concat(newEquipments).value();

    for (const equipment of concatEquipments) {
      if (equipment.locationSector) {
        locationSectors.push(equipment.locationSector);
      }

      if (equipment.locationEnvironment) {
        concatLocationEnvironments.push(equipment.locationEnvironment);
      }
    }

    for (const locationEnvironment of concatLocationEnvironments) {
      if (locationEnvironment.locationSector) {
        locationSectors.push(locationEnvironment.locationSector);
      }
    }

    const equipmentIds = chain(concatEquipments).map('id').uniq().value();
    const locationSectorIds = chain(locationSectors).map('id').uniq().value();
    const locationEnvironmentIds = chain(concatLocationEnvironments).map('id').uniq().value();

    return {
      ...input,
      equipmentIds,
      locationSectorIds,
      locationEnvironmentIds
    };
  }
}
